import './modal.scss';

import axios from 'axios';
import { checkAndLoadFiles, createElementFromHTML } from '../../utilities/js/helper';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

class Modal {
    constructor (element, options) {
        const defaults = {
            initAttr: 'data-modal',
            modalAttr: 'data-modaltarget',
            scrollerAttr: 'scroller',
            onAjaxLoaded: null,
            onClosed: null,
            loader: null
        };

        this.settings = Object.assign({}, defaults, options);
        this.modalType = 'content';

        this.$modal = element;

        this.modalOpen = false;
        this.$modalTarget = null;
        this.$modalScroller = null;

        const modalId = this.$modal.getAttribute('data-modal');

        if (modalId) {
            this.$modalTarget = document.getElementById(modalId);
        }

        this.initialize();
    }

    initialize () {
        this.setEvents();
    }

    setEvents () {
        this.$modal.setAttribute('data-modal-init', 'true');

        this.$modal.addEventListener('click', (e) => {
            e.preventDefault();
            this.openModal();
        });

        if (this.$modal.getAttribute('data-modal-open') !== null) {
            window.setTimeout(() => {
                this.openModal();
            }, 500);
        }

        // open modal on enter or space key
        this.$modal.addEventListener('keydown', (e) => {
            if (e.code === 'Enter' || e.code === 'Space') {
                e.preventDefault();
                this.openModal();
            }
        });

        // close modal on ESC key
        document.addEventListener('keydown', (e) => {
            let isEscape = false;

            if ('key' in e) {
                isEscape = (e.key === 'Escape' || e.key === 'Esc');
            } else {
                isEscape = (e.keyCode === 27);
            }

            if (isEscape && this.modalOpen === true) {
                this.closeModal();
            }
        });
    }

    openModal () {
        this.$modal.setAttribute('aria-pressed', 'true');
        const modalId = this.$modal.getAttribute('data-modal');
        const $modalAjaxURL = this.$modal.getAttribute('data-modal-ajax-url');
        const $currentModal = document.querySelector('.modal.is--active');

        this.$modalTarget = document.getElementById(modalId);

        if ($currentModal) {
            const modalAPI = $currentModal.API;
            modalAPI.closeModal();
        }

        if ($modalAjaxURL !== null) {
            this.loadModal($modalAjaxURL, () => {
                this.showModal();
            });
            return false;
        }

        if (this.$modalTarget === null) {
            const uri = this.$modal.getAttribute('href');
            this.modalType = 'ajax';

            if (uri !== null) {
                this.loadModal(uri, () => {
                    this.showModal();
                });
            }
        } else {
            this.showModal();
        }
    }

    loadModal (uri, callback) {
        let $loader = null;

        if (this.settings.loader !== null) {
            $loader = createElementFromHTML(this.settings.loader);
        }

        if ($loader !== null) {
            this.$modal.appendChild($loader);
        }

        axios({
            url: uri
        }).then((response) => {
            if (typeof response.data !== 'undefined') {
                const content = response.data;
                const $content = createElementFromHTML(content);

                checkAndLoadFiles($content);

                document.querySelector('body').appendChild($content);
                const modalId = $content.getAttribute('id');
                this.$modalTarget = document.getElementById(modalId);

                if ($loader !== null) {
                    $loader.parentNode.removeChild($loader);
                }

                if (typeof this.settings.onAjaxLoaded === 'function') {
                    this.settings.onAjaxLoaded(this.$modalTarget);
                }

                if (typeof callback === 'function') {
                    callback();
                }
            }
        }).catch((error) => {
            throw Error(error);
        });
    }

    showModal () {
        if (this.modalType === 'ajax') {
            window.setTimeout(() => {
                this.$modalTarget.classList.add('is--active');
            }, 250);
        } else {
            this.$modalTarget.classList.add('is--active');
        }

        this.$modalScroller = this.$modalTarget.querySelector('[' + this.settings.modalAttr + '="' + this.settings.scrollerAttr + '"]');
        this.$modalTarget.classList.add('is--active');
        this.$modalTarget.setAttribute('aria-hidden', 'false');

        this.$modalTarget.querySelector('[data-modal-close-btn]').addEventListener('keydown', (e) => {
            if (e.code === 'Enter' || e.code === 'Space') {
                e.preventDefault();
                this.closeModal();
            }
        });

        setTimeout(() => {
            this.$modalTarget.querySelector('[data-modal-close-btn]').focus();
        }, 100);

        if (this.$modalScroller) {
            disableBodyScroll(this.$modalScroller);
        }

        document.addEventListener('keydown', (event) => {
            if (event.code === 'Tab' || event.code === 'Tabs') {
                this.handleOutsideClick(event);
            }
        });

        const $$exists = this.$modalTarget.querySelectorAll('[data-modal-close]');
        $$exists.forEach(($exit) => {
            $exit.addEventListener('click', (e) => {
                e.preventDefault();
                this.closeModal();
            });
        });

        this.modalOpen = true;
        this.$modalTarget.API = this;
    }

    closeModal () {
        this.modalOpen = false;
        this.$modalTarget.classList.remove('is--active');

        setTimeout(() => {
            this.$modalTarget.setAttribute('aria-hidden', 'true');
        }, 100);

        if (this.$modalScroller) {
            enableBodyScroll(this.$modalScroller);
        }

        if (this.modalType === 'ajax') {
            window.setTimeout(() => {
                this.$modalTarget.parentNode.removeChild(this.$modalTarget);
                this.$modalScroller = null;
            }, 250);
        }

        if (typeof this.settings.onClosed === 'function') {
            this.settings.onClosed(this.$modalTarget);
        }

        this.$modal.setAttribute('aria-pressed', 'false');

        const modelClosed = new CustomEvent('modal.closed', {
            view: window,
            bubbles: true,
            cancelable: false,
            detail: {
                $context: this.$modalTarget
            }
        });

        window.dispatchEvent(modelClosed);
    }

    handleOutsideClick (e) {
        if (!this.$modalTarget.contains(e.target) && this.modalOpen === true) {
            this.closeModal();
        }
    }
}

export { Modal };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$modal = $context.querySelectorAll('[data-modal]:not([data-modal-init="true"]), [rel="modal"]');
        for (let i = 0; i < $$modal.length; i++) {
            const $modal = $$modal[i];

            const modalAPI = new Modal($modal);
            $modal.API = modalAPI;
        }
    }
});
